// src/utils/deliveryTaxCalculations.ts - Calculs TVA pour les approvisionnements fournisseurs

import { DeliveryItem, Delivery, Supplier } from '../types/suppliers';
import { TaxRate, TaxSummary } from '../types/tax';
import { Product } from '../types/inventory';
import { Business } from '../types/business';
import { calculateTaxAmounts, findTaxRateById } from './taxCalculations';

/**
 * Enrichit un item d'approvisionnement avec les calculs de TVA
 */
export const enrichDeliveryItemWithTax = (
  item: DeliveryItem,
  supplier: Supplier,
  product: Product | undefined,
  taxRates: TaxRate[],
  defaultTaxRateId: string,
  displayMode: 'inclusive' | 'exclusive'
): DeliveryItem => {
  // ✅ CORRECTION : Utiliser la TVA du produit (priorité : produit > business default)
  const itemTaxRateId = product?.taxRateId || defaultTaxRateId;
  const taxRate = findTaxRateById(taxRates, itemTaxRateId);
  
  if (!taxRate) {
    // Pas de TVA trouvée, retourner l'item tel quel
    return {
      ...item,
      taxRateId: undefined,
      taxRateName: 'Aucune TVA',
      taxRate: 0,
      unitCostExcludingTax: item.unitCost,
      unitCostIncludingTax: item.unitCost,
      taxAmount: 0,
      totalTaxAmount: 0,
      totalCostExcludingTax: item.totalCost,
      totalCostIncludingTax: item.totalCost,
    };
  }

  // Déterminer si le prix du fournisseur inclut la TVA
  const priceIncludesTax = supplier.pricesIncludeTax ?? (displayMode === 'inclusive');
  
  // Calculer les montants TVA
  const calculation = calculateTaxAmounts(item.unitCost, taxRate.rate, priceIncludesTax);
  
  return {
    ...item,
    taxRateId: taxRate.id,
    taxRateName: taxRate.name,
    taxRate: taxRate.rate,
    unitCostExcludingTax: calculation.amountExcludingTax,
    unitCostIncludingTax: calculation.amountIncludingTax,
    taxAmount: calculation.taxAmount,
    totalTaxAmount: calculation.taxAmount * item.quantity,
    totalCostExcludingTax: calculation.amountExcludingTax * item.quantity,
    totalCostIncludingTax: calculation.amountIncludingTax * item.quantity,
  };
};

/**
 * Recalcule tous les totaux de l'approvisionnement avec la TVA
 */
export const calculateDeliveryTotalsWithTax = (
  delivery: Delivery,
  supplier: Supplier,
  products: Product[],
  business: Business | null
): Delivery => {
  if (!business?.configuration?.tax) {
    // Pas de configuration TVA, retourner l'approvisionnement tel quel
    return delivery;
  }

  const taxConfig = business.configuration.tax;
  const taxRates = taxConfig.rates || [];
  const defaultTaxRateId = taxConfig.defaultRateId;
  const displayMode = taxConfig.displayMode;

  // Enrichir chaque item avec les calculs TVA
  const enrichedItems = delivery.items.map(item => {
    const product = products.find(p => p.id === item.productId);
    
    return enrichDeliveryItemWithTax(item, supplier, product, taxRates, defaultTaxRateId, displayMode);
  });

  // Calculer les totaux
  let subtotalExcludingTax = 0;
  let totalTaxAmount = 0;
  let subtotalIncludingTax = 0;
  let originalTotal = 0;

  enrichedItems.forEach(item => {
    subtotalExcludingTax += item.totalCostExcludingTax || 0;
    totalTaxAmount += item.totalTaxAmount || 0;
    subtotalIncludingTax += item.totalCostIncludingTax || 0;
    originalTotal += item.totalCost; // Prix original saisi
  });

  return {
    ...delivery,
    items: enrichedItems,
    totalAmount: originalTotal,
    subtotalExcludingTax: Math.round(subtotalExcludingTax * 100) / 100,
    totalTaxAmount: Math.round(totalTaxAmount * 100) / 100,
    subtotalIncludingTax: Math.round(subtotalIncludingTax * 100) / 100,
  };
};

/**
 * Génère un résumé TVA pour l'approvisionnement
 */
export const generateDeliveryTaxSummary = (delivery: Delivery): TaxSummary[] => {
  const taxGroups = new Map<string, {
    taxRateId: string;
    taxRateName: string;
    rate: number;
    baseAmount: number;
    taxAmount: number;
    totalAmount: number;
  }>();

  delivery.items.forEach(item => {
    const taxRateId = item.taxRateId || 'no-tax';
    const taxRateName = item.taxRateName || 'Sans TVA';
    const rate = item.taxRate || 0;
    
    const itemBaseAmount = item.totalCostExcludingTax || item.totalCost;
    const itemTaxAmount = item.totalTaxAmount || 0;
    const itemTotalAmount = item.totalCostIncludingTax || item.totalCost;

    if (!taxGroups.has(taxRateId)) {
      taxGroups.set(taxRateId, {
        taxRateId,
        taxRateName,
        rate,
        baseAmount: 0,
        taxAmount: 0,
        totalAmount: 0,
      });
    }

    const group = taxGroups.get(taxRateId)!;
    group.baseAmount += itemBaseAmount;
    group.taxAmount += itemTaxAmount;
    group.totalAmount += itemTotalAmount;
  });

  return Array.from(taxGroups.values()).map(group => ({
    taxRateId: group.taxRateId,
    taxRateName: group.taxRateName,
    rate: group.rate,
    baseAmount: Math.round(group.baseAmount * 100) / 100,
    taxAmount: Math.round(group.taxAmount * 100) / 100,
    totalAmount: Math.round(group.totalAmount * 100) / 100,
  }));
};

/**
 * Met à jour les prix d'un item quand l'utilisateur change le coût unitaire
 */
export const updateDeliveryItemCost = (
  item: DeliveryItem,
  newCost: number,
  taxRates: TaxRate[],
  priceIncludesTax: boolean
): DeliveryItem => {
  if (!item.taxRateId) {
    return {
      ...item,
      unitCost: newCost,
      totalCost: newCost * item.quantity,
      unitCostExcludingTax: newCost,
      unitCostIncludingTax: newCost,
      taxAmount: 0,
      totalTaxAmount: 0,
      totalCostExcludingTax: newCost * item.quantity,
      totalCostIncludingTax: newCost * item.quantity,
    };
  }

  const taxRate = findTaxRateById(taxRates, item.taxRateId);
  if (!taxRate) {
    return {
      ...item,
      unitCost: newCost,
      totalCost: newCost * item.quantity,
    };
  }

  const calculation = calculateTaxAmounts(newCost, taxRate.rate, priceIncludesTax);

  return {
    ...item,
    unitCost: newCost,
    totalCost: newCost * item.quantity,
    unitCostExcludingTax: calculation.amountExcludingTax,
    unitCostIncludingTax: calculation.amountIncludingTax,
    taxAmount: calculation.taxAmount,
    totalTaxAmount: calculation.taxAmount * item.quantity,
    totalCostExcludingTax: calculation.amountExcludingTax * item.quantity,
    totalCostIncludingTax: calculation.amountIncludingTax * item.quantity,
  };
};